import React, { useMemo, useState } from 'react';
import { Card, CardBody, Button, Chip } from '@heroui/react';
import {
  Download,
  FileText,
  Image as ImageIcon,
  Video,
  FileAudio,
  Archive,
  File as FileIcon,
  Loader2,
} from 'lucide-react';
import { downloadPrivateFile } from '@/shared/lib/utils/privateFiles';
import { getReadableMimeType } from '@/shared/types/fileTypes';
import type { FileInfo } from '@/shared/types/files';

type Props = {
  /** приватные файлы: скачиваются по ключу, ошибку показывает downloadPrivateFile */
  attachments: FileInfo[];
  className?: string;
};

function formatBytes(bytes?: number) {
  if (bytes === null || bytes === undefined) return null;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const digits = v >= 10 || i === 0 ? 0 : 1;
  return `${v.toFixed(digits)} ${units[i]}`;
}

function getFileCategory(att: FileInfo): 'pdf' | 'image' | 'video' | 'audio' | 'archive' | 'other' {
  const mime = (att.mime || '').toLowerCase();
  const name = (att.name || '').toLowerCase();

  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return 'image';
  if (mime.startsWith('video/') || /\.(mp4|webm|mov|mkv)$/.test(name)) return 'video';
  if (mime.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|flac)$/.test(name)) return 'audio';
  if (
    mime.includes('zip') ||
    mime.includes('rar') ||
    mime.includes('7z') ||
    /\.(zip|rar|7z|tar|gz)$/.test(name)
  ) {
    return 'archive';
  }
  return 'other';
}

function FileTypeIcon({ category }: { category: ReturnType<typeof getFileCategory> }) {
  const cls = 'size-5 text-foreground/80';
  switch (category) {
    case 'pdf':
      return <FileText className={cls} aria-hidden />;
    case 'image':
      return <ImageIcon className={cls} aria-hidden />;
    case 'video':
      return <Video className={cls} aria-hidden />;
    case 'audio':
      return <FileAudio className={cls} aria-hidden />;
    case 'archive':
      return <Archive className={cls} aria-hidden />;
    default:
      return <FileIcon className={cls} aria-hidden />;
  }
}

export function AttachmentsDownloadList({ attachments, className }: Props) {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const items = useMemo(() => attachments ?? [], [attachments]);

  const onDownload = async (att: FileInfo) => {
    setLoadingKey(att.key);
    try {
      await downloadPrivateFile(att);
    } finally {
      setLoadingKey(null);
    }
  };

  if (!items.length) return null;

  return (
    <div className={className}>
      <div className="flex flex-col gap-3">
        {items.map((att) => {
          const category = getFileCategory(att);
          const isLoading = loadingKey === att.key;
          const size = formatBytes(att.size);

          return (
            <Card key={att.key} className="shadow-info-card!">
              <CardBody className="py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-default-100">
                    <FileTypeIcon category={category} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">{att.name}</div>

                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {att.mime ? (
                        <Chip size="sm" variant="flat" className="max-w-full">
                          <span className="truncate">{getReadableMimeType(att.mime)}</span>
                        </Chip>
                      ) : null}

                      {size ? (
                        <Chip size="sm" variant="flat">
                          {size}
                        </Chip>
                      ) : null}

                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant="flat"
                    className="bg-red text-white"
                    isDisabled={isLoading}
                    startContent={
                      isLoading ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                      ) : (
                        <Download className="size-4" aria-hidden />
                      )
                    }
                    onPress={() => onDownload(att)}
                  >
                    {isLoading ? 'Downloading…' : 'Download'}
                  </Button>
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
